import { useLazyQuery, useMutation } from '@apollo/client';
import { gql } from '__generated__';
import dayjs from 'dayjs';
import { FC, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Columns from 'components/Columns';
import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanel } from 'components/FormPanel';
import LoadingIndicator from 'components/LoadingIndicator';

import countries from 'utils/countries-enum.json';

const USER_FRAGMENT = gql(`
  fragment UserFragment on UserType {
    id
    department {
      id
      name
    }
    firstName
    middleName
    lastName
    fullName
    email
    phoneNumber
    gender
    dateOfBirth
    nationality
    role
  }
`);

const GET_USER_QUERY = gql(`
  query User($id: ID!) {
    user(id: $id) {
      id
      department {
        id
        name
      }
      firstName
      middleName
      lastName
      fullName
      email
      phoneNumber
      gender
      dateOfBirth
      nationality
      role
    }
  }
`);

const UPSERT_USER_MUTATION = gql(`
  mutation UpsertUserBasicInformation(
    $id: ID
    $departmentId: ID!
    $firstName: String!
    $middleName: String
    $lastName: String!
    $email: String!
    $phoneNumber: String
    $gender: GenderEnumType!
    $dateOfBirth: String!
    $nationality: NationalityEnumType!
    $role: RoleEnumType!
  ) {
    upsertUser(
      id: $id
      departmentId: $departmentId
      firstName: $firstName
      middleName: $middleName
      lastName: $lastName
      email: $email
      phoneNumber: $phoneNumber
      gender: $gender
      dateOfBirth: $dateOfBirth
      nationality: $nationality
      role: $role
    ) {
      id
      department {
        id
        name
      }
      firstName
      middleName
      lastName
      fullName
      email
      phoneNumber
      gender
      dateOfBirth
      nationality
      role
    }
  }
`);

const BasicInformation: FC = () => {
  const navigate = useNavigate();
  const { departmentId, userId } = useParams<{ departmentId: string; userId: string }>() as {
    departmentId: string;
    userId: string;
  };

  const [getUser, { data, loading, error, refetch }] = useLazyQuery(GET_USER_QUERY);

  const [upsertUser, { loading: upsertLoading, error: upsertError }] = useMutation(
    UPSERT_USER_MUTATION,
    {
      update(cache, { data }) {
        if (!data) return;
        cache.modify({
          id: `DepartmentType:${departmentId}`,
          fields: {
            users(existingUsersRef) {
              const newUserNodeRef = cache.writeFragment({
                data: data.upsertUser,
                fragment: USER_FRAGMENT,
              });
              const updatedUsersRef = { ...existingUsersRef };
              updatedUsersRef.nodes = [newUserNodeRef, ...updatedUsersRef.nodes];
              return updatedUsersRef;
            },
          },
        });
      },
    }
  );

  useEffect(() => {
    if (!userId) return;

    getUser({ variables: { id: userId } });
  }, [userId, getUser]);

  if (loading) return <LoadingIndicator />;

  if (error) return <ErrorMessage error={error} refetch={refetch} />;

  if (userId && !data) return <LoadingIndicator />;

  return (
    <FormPanel
      loading={upsertLoading}
      error={upsertError}
      onSubmit={data => {
        upsertUser({
          variables: {
            id: userId ? userId : undefined,
            departmentId: departmentId,
            firstName: data.firstName,
            middleName: data.middleName,
            lastName: data.lastName,
            email: data.email,
            phoneNumber: data.phoneNumber,
            gender: data.gender,
            dateOfBirth: dayjs(data.dateOfBirth).toISOString(),
            nationality: data.nationality,
            role: data.role,
          },
        }).then(data => {
          navigate(
            `/create/departments/${departmentId}/user/${data.data?.upsertUser?.id}/employee-information`
          );
        });
      }}
      submitButtonLabel="Save and continue"
    >
      <h2>Basic Information</h2>
      <Columns number={3}>
        <FormInput
          fullWidth
          type="string"
          fieldName="firstName"
          label="First Name"
          defaultValue={data?.user?.firstName || ''}
          validators={{
            required: true,
          }}
        />
        <FormInput
          fullWidth
          type="string"
          fieldName="middleName"
          label="Middle Name"
          defaultValue={data?.user?.middleName || ''}
        />
        <FormInput
          fullWidth
          type="string"
          fieldName="lastName"
          label="Last Name"
          defaultValue={data?.user?.lastName || ''}
          validators={{
            required: true,
          }}
        />
      </Columns>
      <Columns number={2}>
        <FormInput
          fullWidth
          type="string"
          fieldName="email"
          label="Email"
          defaultValue={data?.user?.email || ''}
          validators={{
            required: true,
            isEmail: true,
          }}
        />
        <FormInput
          fullWidth
          type="string"
          fieldName="phoneNumber"
          label="Phone Number"
          defaultValue={data?.user?.phoneNumber || ''}
          validators={{
            required: true,
          }}
        />
      </Columns>
      <Columns number={2}>
        <FormInput
          fullWidth
          type="select"
          fieldName="gender"
          label="Gender"
          defaultValue={data?.user?.gender || ''}
          options={[
            { value: 'MALE', label: 'Male' },
            { value: 'FEMALE', label: 'Female' },
            { value: 'OTHER', label: 'Other' },
          ]}
          validators={{
            required: true,
          }}
        />
        <FormInput
          fullWidth
          type="date"
          fieldName="dateOfBirth"
          label="Date of Birth"
          defaultValue={data?.user?.dateOfBirth || ''}
          validators={{
            required: true,
          }}
        />
      </Columns>
      <Columns number={2}>
        <FormInput
          fullWidth
          type="select"
          fieldName="nationality"
          label="Nationality"
          defaultValue={data?.user?.nationality || 'PAPUA_NEW_GUINEA'}
          options={
            Object.keys(countries).map(key => ({
              value: key,
              label: countries[key],
            })) as { value: string; label: string }[]
          }
          validators={{
            required: true,
          }}
        />
        <FormInput
          fullWidth
          type="select"
          fieldName="role"
          label="Role"
          helperText="The role here refers to the permission roles of this dashboard not the person's organisation"
          defaultValue={data?.user?.role || 'EMPLOYEE'}
          options={[{ value: 'EMPLOYEE', label: 'Employee' }]}
          validators={{
            required: true,
          }}
        />
      </Columns>
    </FormPanel>
  );
};

export default BasicInformation;
